import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, Search, X, Check, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function fmt(d) { try { return new Date(d).toLocaleString("en-GB"); } catch { return d; } }

const decisionBadge = (d) => ({
  Pending: "bg-amber-100 text-amber-700", Cleared: "bg-emerald-100 text-emerald-700",
  Declined: "bg-red-100 text-red-700", Override: "bg-blue-100 text-blue-700",
}[d] || "bg-slate-100 text-slate-600");

export default function ConflictCheckModal({ subject, linkedType, linkedId, onClose }) {
  const { can } = useAuth();
  const canDecide = can("compliance", "create");
  const [form, setForm] = useState({ name: subject?.name || "", email: subject?.email || "", phone: subject?.phone || "" });
  const [matches, setMatches] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState("");

  const loadHistory = useCallback(() => {
    if (!linkedId) return;
    api.get(`/admin/conflict-checks`, { params: { linked_id: linkedId } }).then((r) => setHistory(r.data)).catch(() => {});
  }, [linkedId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const runSearch = async () => {
    try {
      const { data } = await api.post("/admin/conflict-checks/search", form);
      setMatches(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const record = async () => {
    try {
      await api.post("/admin/conflict-checks", {
        subject_name: form.name, subject_email: form.email, subject_phone: form.phone,
        linked_type: linkedType, linked_id: linkedId, matches: matches || [], notes,
      });
      toast.success("Conflict check recorded (Pending review)");
      setNotes(""); loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const decide = async (id, decision) => {
    try {
      await api.patch(`/admin/conflict-checks/${id}/decision`, { decision });
      toast.success(`Marked ${decision}`);
      loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="conflict-check-modal" className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-navy"><ShieldAlert className="text-gold" size={20} /> Conflict Check</h2>
          <button onClick={onClose} data-testid="conflict-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <p className="mb-3 text-xs text-slate-500">Search existing records before opening a matter. Possible matches are shown for human review — a conflict is never declared automatically.</p>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Input data-testid="conflict-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" />
          <Input data-testid="conflict-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" />
          <Input data-testid="conflict-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" />
        </div>
        <button onClick={runSearch} data-testid="conflict-search-btn" className="btn-navy mt-3 w-full text-sm"><Search size={15} /> Run search</button>

        {matches !== null && (
          <div className="mt-4">
            <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Possible matches ({matches.length})</div>
            {matches.length ? (
              <div className="space-y-2">
                {matches.map((m, i) => (
                  <div key={i} data-testid="conflict-match" className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs">
                    <span className="font-medium text-navy">{m.name}</span> <span className="text-slate-500">· {m.source}</span>
                    <div className="text-slate-500">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                  </div>
                ))}
              </div>
            ) : <p className="rounded-lg bg-emerald-50 p-2.5 text-xs text-emerald-700">No existing matches found.</p>}
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reviewer notes (optional)…" rows={2} className="mt-3" data-testid="conflict-notes" />
            <button onClick={record} data-testid="conflict-record-btn" className="btn-gold mt-2 w-full text-sm">Record this check</button>
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Check history</div>
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.id} data-testid="conflict-history-row" className="rounded-lg border border-slate-100 p-2.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">{fmt(h.created_at)} · {h.matches?.length || 0} match(es)</span>
                    <span className={`rounded-full px-2 py-0.5 font-medium ${decisionBadge(h.decision)}`}>{h.decision}</span>
                  </div>
                  {h.notes && <div className="mt-1 text-slate-500">{h.notes}</div>}
                  {h.reviewer && <div className="mt-0.5 text-slate-400">Reviewed by {h.reviewer}</div>}
                  {canDecide && h.decision === "Pending" && (
                    <div className="mt-2 flex gap-2">
                      <button onClick={() => decide(h.id, "Cleared")} data-testid="conflict-clear-btn" className="flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white"><Check size={13} /> Clear</button>
                      <button onClick={() => decide(h.id, "Declined")} data-testid="conflict-decline-btn" className="flex items-center gap-1 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white"><Ban size={13} /> Decline</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
